import { getRuntimeMode, openExternalUrl, type RuntimeMode } from './runtimeCapabilities';

export const ZIAWELLNAV_TELEGRAM_APP_URL = 'https://t.me/vamosar_race_bot/app';

export type TelegramLaunchFeature =
  | 'company_dashboard'
  | 'forms'
  | 'ai'
  | 'fleet'
  | 'timesheets'
  | 'meetings'
  | 'policies'
  | 'training'
  | 'gps'
  | 'cert_vault'
  | 'crew'
  | 'generic';

export interface BuildTelegramOpenUrlOptions {
  feature?: TelegramLaunchFeature;
  startApp?: string | null;
}

export interface TelegramRequiredAccessCopy {
  title: string;
  message: string;
  chromeDemoContext: string;
  actionLabel: string;
  url: string;
  mode: RuntimeMode;
  feature?: TelegramLaunchFeature;
}

export type OpenInTelegramCopy = TelegramRequiredAccessCopy;

export interface TelegramRequiredPayload {
  error?: unknown;
  message?: unknown;
  hint?: unknown;
  authMode?: unknown;
  actionLabel?: unknown;
  telegramOpenUrl?: unknown;
}

export class TelegramRequiredError extends Error {
  status?: number;
  payload?: unknown;
  url: string;
  actionLabel: string;
  isTelegramRequiredError = true;

  constructor(message: string, options: { status?: number; payload?: unknown; url?: string; actionLabel?: string } = {}) {
    super(message);
    this.name = 'TelegramRequiredError';
    this.status = options.status;
    this.payload = options.payload;
    this.url = options.url || ZIAWELLNAV_TELEGRAM_APP_URL;
    this.actionLabel = options.actionLabel || 'Open in Telegram';
  }
}

const TELEGRAM_REQUIRED_PATTERNS = [
  /authentication required/i,
  /user authentication required/i,
  /telegram signed/i,
  /signed telegram/i,
  /x-telegram-init-data/i,
  /open ziawellnav inside telegram/i,
  /open telegram/i,
  /open in telegram/i,
  /full telegram mini app signed session/i,
  /requires signed telegram mini app access/i,
  /requires telegram mini app signed access/i,
  /telegram initdata missing/i,
];

const asPayload = (value: unknown): TelegramRequiredPayload | null => {
  if (!value || typeof value !== 'object') return null;
  return value as TelegramRequiredPayload;
};

const payloadText = (payload: TelegramRequiredPayload): string => [
  payload.error,
  payload.message,
  payload.hint,
].filter((value) => typeof value === 'string').join(' ');

const sourceText = (source: unknown): string => {
  if (typeof source === 'string') return source;
  if (source instanceof Error) return source.message;
  const payload = source instanceof TelegramRequiredError
    ? asPayload(source.payload)
    : asPayload(source);
  return payload ? payloadText(payload) : '';
};

const sanitizeStartApp = (value?: string | null): string => {
  const trimmed = String(value || '').trim();
  return /^[A-Za-z0-9_-]{1,64}$/.test(trimmed) ? trimmed : '';
};

export function buildTelegramOpenUrl(options: BuildTelegramOpenUrlOptions = {}): string {
  const startApp = sanitizeStartApp(options.startApp);
  if (!startApp) return ZIAWELLNAV_TELEGRAM_APP_URL;
  try {
    const url = new URL(ZIAWELLNAV_TELEGRAM_APP_URL);
    url.searchParams.set('startapp', startApp);
    return url.toString();
  } catch {
    return `${ZIAWELLNAV_TELEGRAM_APP_URL}?startapp=${encodeURIComponent(startApp)}`;
  }
}

export function inferTelegramLaunchFeature(value: unknown): TelegramLaunchFeature {
  const text = sourceText(value).toLowerCase();
  if (/gps|geolocation|location api|phone gps/.test(text)) return 'gps';
  if (/ai|rag|knowledge|model|provider|llama|gemma|qwen|grok|openai|ollama/.test(text)) return 'ai';
  if (/meeting|rsvp|check in|check-in/.test(text)) return 'meetings';
  if (/policy|policies|acknowledge/.test(text)) return 'policies';
  if (/training|quiz|course|lesson/.test(text)) return 'training';
  if (/fleet|asset|pm alert|shop request|routing/.test(text)) return 'fleet';
  if (/timesheet|time entr|timer|work timer/.test(text)) return 'timesheets';
  if (/form|jsa|submission|inspection/.test(text)) return 'forms';
  if (/cert|license|licence|vault/.test(text)) return 'cert_vault';
  if (/crew|member|profile|worker|company dashboard|business seat|company record|company/.test(text)) return 'company_dashboard';
  return 'generic';
}

const featureCopy: Record<TelegramLaunchFeature, Omit<OpenInTelegramCopy, 'url' | 'mode' | 'feature'>> = {
  company_dashboard: {
    title: 'Open Company Dashboard in Telegram',
    message: 'Company records require signed Telegram Mini App access. Open @vamosar_race_bot to use the full company dashboard safely.',
    chromeDemoContext: 'Chrome Demo previews public navigation; Telegram unlocks company records, seats, fleet, forms, timesheets, policies, training, meetings, and bot notifications.',
    actionLabel: 'Open in Telegram',
  },
  forms: {
    title: 'Open Forms in Telegram',
    message: 'Open in Telegram to submit signed company records for Forms, JSAs, inspections, and worker history.',
    chromeDemoContext: 'Chrome Demo can preview public navigation; Telegram is required for company-synced form submissions and signed records.',
    actionLabel: 'Open in Telegram',
  },
  ai: {
    title: 'Open AI in Telegram',
    message: 'Open in Telegram to use AI and company knowledge safely with your signed Mini App session.',
    chromeDemoContext: 'Chrome Demo does not access private company records or AI/RAG. Telegram unlocks authorized knowledge, sources, and audit-safe AI tools.',
    actionLabel: 'Open in Telegram',
  },
  fleet: {
    title: 'Open Fleet in Telegram',
    message: 'Fleet management requires signed Telegram Mini App access for company assets, routing history, shop requests, and PM records.',
    chromeDemoContext: 'Chrome Demo stays public; Telegram unlocks company fleet records and bot-backed routing notifications.',
    actionLabel: 'Open in Telegram',
  },
  timesheets: {
    title: 'Open Timesheets in Telegram',
    message: 'Open in Telegram to start work timers, submit timesheets, and keep signed worker time history.',
    chromeDemoContext: 'Chrome Demo cannot write private worker records. Telegram unlocks signed time entries, submissions, and company timesheet history.',
    actionLabel: 'Open in Telegram',
  },
  meetings: {
    title: 'Open Meetings in Telegram',
    message: 'Open Telegram to receive bot notifications, meeting updates, RSVP, check in, and view meeting locations.',
    chromeDemoContext: 'Chrome Demo can preview public tools; Telegram unlocks company meeting delivery, worker notifications, and attendance records.',
    actionLabel: 'Open in Telegram',
  },
  policies: {
    title: 'Open Policies in Telegram',
    message: 'Open in Telegram to review, sign, issue, and access company policies with authenticated employee sign-off.',
    chromeDemoContext: 'Chrome Demo cannot sign private policy records. Telegram unlocks policy assignments, acknowledgements, revisions, and company records.',
    actionLabel: 'Open in Telegram',
  },
  training: {
    title: 'Open Training in Telegram',
    message: 'Open in Telegram to complete assigned training, quizzes, and acknowledgements with your signed worker profile.',
    chromeDemoContext: 'Chrome Demo previews public navigation; Telegram unlocks training assignments, quiz history, and company completion records.',
    actionLabel: 'Open in Telegram',
  },
  gps: {
    title: 'Open Telegram for phone GPS',
    message: 'GPS works in the full Telegram Mini App. Chrome Demo may not provide phone GPS here. Open @vamosar_race_bot on your phone for GPS navigation.',
    chromeDemoContext: 'Chrome Demo can preview public navigation, but phone GPS is most reliable inside the Telegram Mini App.',
    actionLabel: 'Open in Telegram',
  },
  cert_vault: {
    title: 'Open Cert Vault in Telegram',
    message: 'Open in Telegram to sync certificates and licenses to your private signed Cert Vault.',
    chromeDemoContext: 'Chrome Demo does not sync private worker documents. Telegram unlocks certificate uploads, license history, and company-visible compliance records.',
    actionLabel: 'Open in Telegram',
  },
  crew: {
    title: 'Open Crew Records in Telegram',
    message: 'Open in Telegram to update linked crew, profile, and worker records with a signed Mini App session.',
    chromeDemoContext: 'Chrome Demo stays public; Telegram unlocks linked crew records, worker GPS sharing controls, and private notifications.',
    actionLabel: 'Open in Telegram',
  },
  generic: {
    title: 'Open in Telegram for this feature',
    message: 'This feature works in ZiaWellNav, but it requires the full Telegram Mini App signed session. Open @vamosar_race_bot and try again.',
    chromeDemoContext: 'Chrome Demo previews public navigation; Telegram unlocks company records, forms, AI, timesheets, fleet, policies, training, meetings, and bot notifications.',
    actionLabel: 'Open in Telegram',
  },
};

export function formatOpenInTelegramCopy(
  input?: TelegramLaunchFeature | (BuildTelegramOpenUrlOptions & { reason?: unknown })
): OpenInTelegramCopy {
  const feature = typeof input === 'string'
    ? input
    : input?.feature || inferTelegramLaunchFeature(input?.reason);
  const copy = featureCopy[feature] || featureCopy.generic;
  return {
    ...copy,
    url: buildTelegramOpenUrl(typeof input === 'string' ? { feature } : { feature, startApp: input?.startApp }),
    mode: getRuntimeMode(),
    feature,
  };
}

export function isTelegramRequiredPayload(value: unknown, status?: number): boolean {
  const payload = asPayload(value);
  if (!payload) return false;
  if (payload.authMode === 'telegram_signed') return true;
  const text = payloadText(payload);
  return (status === 401 || status == null) && TELEGRAM_REQUIRED_PATTERNS.some((pattern) => pattern.test(text));
}

export function isTelegramRequiredMessage(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  return TELEGRAM_REQUIRED_PATTERNS.some((pattern) => pattern.test(value));
}

export function isTelegramRequiredError(value: unknown): value is TelegramRequiredError {
  if (value instanceof TelegramRequiredError) return true;
  if (!value || typeof value !== 'object') return false;
  const maybeError = value as { isTelegramRequiredError?: unknown; message?: unknown; payload?: unknown; status?: unknown };
  if (maybeError.isTelegramRequiredError === true) return true;
  if (isTelegramRequiredPayload(maybeError.payload, typeof maybeError.status === 'number' ? maybeError.status : undefined)) return true;
  return isTelegramRequiredMessage(maybeError.message);
}

export function formatTelegramRequiredAccess(source?: unknown, feature?: TelegramLaunchFeature): TelegramRequiredAccessCopy {
  const payload = source instanceof TelegramRequiredError
    ? asPayload(source.payload)
    : asPayload(source);
  const contextual = formatOpenInTelegramCopy({ feature: feature || inferTelegramLaunchFeature(source), reason: source });
  const actionLabel = String(payload?.actionLabel || (source instanceof TelegramRequiredError ? source.actionLabel : '') || contextual.actionLabel);
  const url = String(payload?.telegramOpenUrl || (source instanceof TelegramRequiredError ? source.url : '') || contextual.url);
  return {
    ...contextual,
    actionLabel,
    url,
  };
}

export function toTelegramRequiredError(payload: unknown, status?: number, feature?: TelegramLaunchFeature): TelegramRequiredError {
  const copy = formatTelegramRequiredAccess(payload, feature);
  return new TelegramRequiredError(copy.message, {
    status,
    payload,
    url: copy.url,
    actionLabel: copy.actionLabel,
  });
}

export function throwTelegramAwareError(payload: unknown, status: number, fallbackMessage: string): never {
  if (isTelegramRequiredPayload(payload, status)) {
    throw toTelegramRequiredError(payload, status, inferTelegramLaunchFeature(fallbackMessage));
  }
  const typed = asPayload(payload);
  const message = typeof typed?.message === 'string'
    ? typed.message
    : typeof typed?.error === 'string'
      ? typed.error
      : fallbackMessage;
  throw new Error(message);
}

export function openZiaWellNavTelegram(url = ZIAWELLNAV_TELEGRAM_APP_URL) {
  return openExternalUrl(url, { tryTelegram: false });
}
